<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <p class="control" style="padding-top: 0.5rem;">
              <span class="subtitle">
                {{ $t('attributes.gift_purchases') }}
              </span>
            </p>
          </b-field>
          <b-field>
            <b-switch v-model="showVoid" @input="onFilter">
              {{ $t('attributes.gift_transfer_show_void') }}
            </b-switch>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            :disabled="!hasPermission(PERMISSION_TYPE.GiftsPurchasesCreate)"
            @click="onAdd"
          >
            {{ $t('attributes.gift_purchase') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :loading="loadingItems || sending"
      :mobile-cards="false"
      scrollable
      hoverable
      narrowed
      paginated
      pagination-position="bottom"
      backend-pagination
      backend-sorting
      height="450"
      sticky-header
      :row-class="rowClass"
      :total="total"
      :current-page="page"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="id"
        :visible="false"
        :label="$t('attributes.id')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.id }}
      </b-table-column>

      <b-table-column
        field="gift_name"
        :label="$t('attributes.gift_name')"
        width="120"
        sortable
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{ props.row.gift.full_name }}
      </b-table-column>

      <b-table-column
        field="date"
        :label="$t('attributes.gift_purchase_date')"
        width="120"
        centered
        sortable
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{ formatDate(props.row.date) }}
      </b-table-column>

      <b-table-column
        field="quantity"
        :label="$t('attributes.gift_quantity')"
        width="80"
        numeric
        header-class="nowrap"
        v-slot="props"
      >
        {{ props.row.quantity }}
      </b-table-column>

      <b-table-column
        field="net_price"
        :label="$t('attributes.gift_net_price')"
        width="80"
        centered
        header-class="nowrap"
        v-slot="props"
      >
        <span
          v-if="props.row.net_price !== props.row.origin_net_price"
          class="nowrap has-text-danger"
        >
          {{ props.row.origin_net_price }} -> {{ props.row.net_price }}
        </span>
        <span v-else>
          {{ props.row.net_price }}
        </span>
      </b-table-column>

      <b-table-column
        field="cost"
        :label="$t('attributes.gift_unit_cost')"
        width="80"
        centered
        header-class="nowrap"
        v-slot="props"
      >
        {{ props.row.cost }}
      </b-table-column>

      <b-table-column
        field="amount"
        :label="$t('attributes.gift_amount')"
        width="80"
        numeric
        header-class="nowrap"
        v-slot="props"
      >
        {{ props.row.amount }}
      </b-table-column>

      <b-table-column
        field="state"
        :label="$t('attributes.status')"
        width="80"
        centered
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{ orderState(props.row) }}
      </b-table-column>

      <b-table-column
        field="order_number"
        :label="$t('attributes.gift_order_number')"
        width="100"
        centered
        sortable
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{ props.row.order_number }}
      </b-table-column>

      <b-table-column
        field="order_date"
        :label="$t('attributes.gift_order_date')"
        width="120"
        centered
        sortable
        header-class="nowrap"
        v-slot="props"
      >
        {{ formatDateTime(props.row.order_date) }}
      </b-table-column>

      <b-table-column
        field="voucher_number"
        :label="$t('attributes.gift_voucher_number')"
        width="100"
        centered
        sortable
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{ props.row.voucher_number }}
      </b-table-column>

      <b-table-column
        field="voucher_date"
        :label="$t('attributes.gift_voucher_date')"
        width="120"
        centered
        sortable
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{ formatDate(props.row.voucher_date) }}
      </b-table-column>

      <b-table-column
        field="employee_code"
        :label="$t('attributes.gift_updater')"
        width="120"
        sortable
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{
          !!props.row.employee_code
            ? `[${props.row.employee_code}] ${props.row.employee_name}`
            : '無'
        }}
      </b-table-column>

      <b-table-column
        field="updated_at"
        :label="$t('attributes.gift_updated_at')"
        width="120"
        centered
        sortable
        header-class="nowrap"
        v-slot="props"
      >
        {{ formatDateTime(props.row.updated_at) }}
      </b-table-column>

      <b-table-column
        label="功能"
        centered
        width="300"
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        <div v-if="isNewOrder(props.row)" class="buttons is-centered">
          <b-button
            :disabled="!canUpdate(props.row)"
            type="is-second"
            size="is-small"
            @click.prevent="onModify(props.row)"
          >
            {{ $t('attributes.edit') }}
          </b-button>
          <b-button
            :disabled="!canOrdering(props.row)"
            type="is-primary"
            size="is-small"
            @click.prevent="onGenerateOrderNumber(props.row)"
          >
            {{ $t('attributes.gift_generate_order') }}
          </b-button>
          <b-button
            :disabled="!canDelete(props.row)"
            type="is-danger"
            icon-left="delete"
            size="is-small"
            @click.prevent="onDelete(props.row)"
          >
            {{ $t('attributes.delete') }}
          </b-button>
        </div>
        <div v-else-if="isVoid(props.row)" class="buttons is-centered">
          <b-button
            :disabled="!canClone(props.row)"
            type="is-info"
            icon-left="plus"
            size="is-small"
            @click.prevent="onClone(props.row)"
          >
            {{ $t('attributes.gift_clone_voucher') }}
          </b-button>
        </div>
        <div v-else class="buttons is-centered">
          <b-button
            :disabled="!canImport(props.row)"
            type="is-primary"
            size="is-small"
            @click.prevent="onImport(props.row)"
          >
            補傳票
          </b-button>
          <b-button
            type="is-primary"
            size="is-small"
            :disabled="props.row.quantity == 0"
            @click.prevent="onCreatePurchaseOrder(props.row)"
          >
            下載進貨單
          </b-button>
          <b-button
            :disabled="!canVoid(props.row)"
            icon-left="close"
            type="is-danger"
            size="is-small"
            @click.prevent="onVoid(props.row)"
          >
            {{ $t('attributes.gift_void_voucher') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <gift-purchase-form
        :gift-purchase="selected"
        :sending="sending"
        @send="onSend"
      />
    </b-modal>

    <b-modal
      :active.sync="isImportFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <gift-purchase-import-form
        :gift-purchase="selected"
        :sending="sending"
        @send="importItem"
      />
    </b-modal>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { GIFT_PURCHASES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import GiftPurchaseForm from './GiftPurchaseForm'
import GiftPurchaseImportForm from './GiftPurchaseImportForm'
import { mapGetters } from 'vuex'

export default {
  name: 'GiftPurchaseList',
  components: { GiftPurchaseForm, GiftPurchaseImportForm },

  props: {
    consumable: {
      type: Boolean
    },
    search: {
      type: String
    },
    gifts: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      loadingItems: false,
      total: 0,
      items: [],
      selected: null,

      showVoid: false,
      sortField: 'updated_at',
      sortOrder: 'desc',
      defaultSortOrder: 'desc',
      page: 1,
      perPage: 10,

      isFormModalActive: false,
      isImportFormModalActive: false,
      sending: false
    }
  },
  watch: {
    gifts(items) {
      this.loadItems(this.consumable, items)
    },
    consumable(consumable) {
      this.loadItems(consumable, this.gifts)
    }
  },
  computed: {
    ...mapGetters('app', ['hasPermission'])
  },
  mounted() {
    this.loadItems(this.consumable, this.gifts)
  },
  methods: {
    async loadItems(consumable, items) {
      try {
        const api = `${GIFT_PURCHASES_API}`
        const params = {
          consumable: consumable,
          hide_void: !this.showVoid,
          gifts: items.map(g => g.id).join(','),
          search: this.search,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('gift_purchases', e)
      } finally {
        this.loadingItems = false
      }
    },
    onFilter: debounce(function() {
      this.page = 1
      this.loadItems(this.consumable, this.gifts)
    }, 500),
    onPageChange(page) {
      this.page = page
      this.loadItems(this.consumable, this.gifts)
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.loadItems(this.consumable, this.gifts)
    },
    onAdd() {
      this.selected = {
        id: null,
        gift_id: null,
        date: null,
        quantity: null,
        net_price: null,
        cost: null,
        voucher_number: null,
        voucher_date: null
      }
      this.isFormModalActive = true
    },
    onModify(item) {
      this.selected = item
      this.isFormModalActive = true
    },
    onImport(item) {
      this.selected = item
      this.isImportFormModalActive = true
    },
    onGenerateOrderNumber(item) {
      this.selected = item
      this.$buefy.dialog.confirm({
        message: '確定產生單號？',
        type: 'is-warning',
        onConfirm: () => {
          this.orderItem(item)
        }
      })
    },
    onDelete(item) {
      this.selected = item
      this.$buefy.dialog.confirm({
        message: this.$t('attributes.confirm_delete'),
        type: 'is-danger',
        cancelText: this.$t('attributes.cancel'),
        confirmText: this.$t('attributes.delete'),
        onConfirm: () => {
          this.deleteItem(item)
        }
      })
    },
    onVoid(item) {
      this.selected = item

      this.$buefy.dialog.confirm({
        message: this.$t('attributes.gift_purchase_create_again'),
        cancelText: this.$t('attributes.no'),
        confirmText: this.$t('attributes.yes'),
        onConfirm: () => {
          this.voidConfirm(item, true)
        },
        onCancel: () => {
          this.voidConfirm(item, false)
        }
      })
    },
    onClone(item) {
      this.selected = {
        id: null,
        gift_id: item.gift_id,
        date: null,
        quantity: item.quantity,
        net_price: item.net_price,
        cost: item.cost
      }
      this.isFormModalActive = true
    },
    voidConfirm(item, isClone) {
      const message = isClone
        ? '作廢後將會提供重新下單的填寫視窗，確定作廢？'
        : '確定作廢？'

      this.$buefy.dialog.confirm({
        title: '確認視窗',
        message,
        type: 'is-danger',
        cancelText: this.$t('attributes.cancel'),
        confirmText: this.$t('attributes.confirm'),
        onConfirm: () => {
          this.voidItem(item, isClone)
        }
      })
    },
    onSend(item) {
      if (this.selected.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    async createItem(item) {
      try {
        this.sending = true
        const res = await this.axios.post(GIFT_PURCHASES_API, item)
        const data = res.data.data
        if (data !== null) {
          await this.onCreatePurchaseOrder(data)
        }
        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadItems(this.consumable, this.gifts)
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
        this.isFormModalActive = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${GIFT_PURCHASES_API}/${this.selected.id}`

        this.sending = true
        await this.axios.put(api, item)
        notifyService.success(this.$t('attributes.update_succeed'))

        await this.loadItems(this.consumable, this.gifts)
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
        this.isFormModalActive = false
      }
    },
    async orderItem(item) {
      try {
        const api = `${GIFT_PURCHASES_API}/${item.id}/order`

        this.sending = true
        await this.axios.put(api, {})
        notifyService.success(this.$t('attributes.update_succeed'))

        await this.loadItems(this.consumable, this.gifts)
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async importItem(item) {
      try {
        const api = `${GIFT_PURCHASES_API}/${this.selected.id}/import`

        this.sending = true
        await this.axios.put(api, item)
        notifyService.success(this.$t('attributes.update_succeed'))

        this.$emit('updated')
        await this.loadItems(this.consumable, this.gifts)
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
        this.isImportFormModalActive = false
      }
    },
    async deleteItem(item) {
      try {
        const api = `${GIFT_PURCHASES_API}/${item.id}`

        this.sending = true
        await this.axios.delete(api)
        notifyService.success(this.$t('attributes.delete_succeed'))

        await this.loadItems(this.consumable, this.gifts)
      } catch (e) {
        notifyService.delete_error(e)
      } finally {
        this.sending = false
      }
    },
    async voidItem(item, isClone) {
      try {
        const api = `${GIFT_PURCHASES_API}/${item.id}/void`

        this.sending = true
        await this.axios.delete(api)
        notifyService.success(this.$t('attributes.void_succeed'))

        this.$emit('updated')
        await this.loadItems(this.consumable, this.gifts)

        if (isClone) {
          this.selected = {
            id: null,
            gift_id: item.gift_id,
            gift_name: item.gift_name,
            date: item.date,
            quantity: item.quantity,
            net_price: item.net_price,
            cost: item.cost
          }
          this.isFormModalActive = true
        }
      } catch (e) {
        notifyService.void_error(e)
      } finally {
        this.sending = false
      }
    },
    async onCreatePurchaseOrder(item) {
      const api = `${GIFT_PURCHASES_API}/${item.id}/purchase-order-export`
      const config = {
        headers: { Accept: 'application/pdf' },
        responseType: 'blob',
        errorHandle: {
          customMessage: `${this.$t('attributes.fetch_failed', [
            this.$t('attributes.order')
          ])}: `
        }
      }
      try {
        this.loadingItems = true
        this.$emit('update:loading', true)

        const response = await this.axios.get(api, config)
        const fileName = `進貨單-${item.order_number}.pdf`

        const blob = new Blob([response.data], { type: response.data.type })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        notifyService.error('下載進貨單失敗', e)
      } finally {
        this.loadingItems = false
        this.$emit('update:loading', false)
      }
    },
    isNewOrder(purchase) {
      return purchase.state === 'new'
    },
    isVoid(purchase) {
      return purchase.state === 'void'
    },
    canUpdate(purchase) {
      return (
        purchase.state === 'new' &&
        this.hasPermission(this.PERMISSION_TYPE.GiftsPurchasesUpdate)
      )
    },
    canOrdering(purchase) {
      return (
        purchase.state === 'new' &&
        this.hasPermission(this.PERMISSION_TYPE.GiftsPurchasesGenerate)
      )
    },
    canDelete(purchase) {
      return (
        purchase.state === 'new' &&
        this.hasPermission(this.PERMISSION_TYPE.GiftsPurchasesDelete)
      )
    },
    canImport(purchase) {
      return (
        purchase.state === 'pending' &&
        this.hasPermission(this.PERMISSION_TYPE.GiftsPurchasesImport)
      )
    },
    canVoid(purchase) {
      return (
        (purchase.state === 'pending' || purchase.state === 'imported') &&
        this.hasPermission(this.PERMISSION_TYPE.GiftsPurchasesVoid)
      )
    },
    canClone(purchase) {
      return (
        purchase.state === 'void' &&
        this.hasPermission(this.PERMISSION_TYPE.GiftsPurchasesCreate)
      )
    },
    rowClass(row) {
      switch (row.state) {
        case 'new':
          return ''
        case 'pending':
          return 'has-background-danger-light'
        case 'imported':
          return ''
        case 'void':
          return 'has-text-grey-light'
      }

      return ''
    },
    orderState(purchase) {
      switch (purchase.state) {
        case 'new':
          return '新訂單'
        case 'pending':
          return '待處理'
        case 'imported':
          return '已進貨'
        case 'void':
          return '已作廢'
        case 'adjust':
          return '數量調整'
        case 'init':
          return '初始數量'
      }

      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
